import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardFooter, Button, Spinner, Badge } from '../../../shared/components/ui';
import { settingsApi } from '../../../shared/services/settingsApi';

/**
 * Normalize weights to ensure they sum to 100
 */
function normalizeWeights(weights, changedId, newValue) {
  const MIN_WEIGHT = 1;
  const MAX_WEIGHT = 98;

  const clampedValue = Math.max(MIN_WEIGHT, Math.min(MAX_WEIGHT, newValue));
  const remaining = 100 - clampedValue;

  const otherIds = Object.keys(weights).filter(id => id !== changedId);
  const otherSum = otherIds.reduce((sum, id) => sum + weights[id], 0);

  if (otherSum === 0) {
    const evenWeight = remaining / otherIds.length;
    const normalized = { [changedId]: clampedValue };
    otherIds.forEach(id => {
      normalized[id] = Math.max(MIN_WEIGHT, evenWeight);
    });
    return normalized;
  }

  const ratio = remaining / otherSum;
  const normalized = { [changedId]: clampedValue };

  let adjustedSum = clampedValue;
  otherIds.forEach((id, index) => {
    if (index === otherIds.length - 1) {
      normalized[id] = Math.max(MIN_WEIGHT, Math.round((100 - adjustedSum) * 10) / 10);
    } else {
      const adjusted = Math.max(MIN_WEIGHT, Math.round(weights[id] * ratio * 10) / 10);
      normalized[id] = adjusted;
      adjustedSum += adjusted;
    }
  });

  return normalized;
}

/**
 * Editable metric row for admin
 */
function MetricRow({ metric, weight, onWeightChange, isEditing, onEditStart, onEditEnd }) {
  const inputRef = useRef(null);
  const [localValue, setLocalValue] = useState(weight.toString());

  useEffect(() => {
    if (!isEditing) {
      setLocalValue(weight.toString());
    }
  }, [weight, isEditing]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleInputBlur = () => {
    const numValue = parseFloat(localValue);
    if (!isNaN(numValue) && numValue >= 1 && numValue <= 98) {
      onWeightChange(metric.id, numValue);
    } else {
      setLocalValue(weight.toString());
    }
    onEditEnd();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleInputBlur();
    } else if (e.key === 'Escape') {
      setLocalValue(weight.toString());
      onEditEnd();
    }
  };

  return (
    <tr className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800">
      <td className="px-4 py-3">
        <div className="font-medium text-gray-900 dark:text-gray-100">{metric.displayName}</div>
        <div className="text-sm text-gray-500 dark:text-gray-400">{metric.description}</div>
      </td>
      <td className="px-4 py-3 text-right w-32">
        {isEditing ? (
          <div className="flex items-center justify-end">
            <input
              ref={inputRef}
              type="number"
              min="1"
              max="98"
              step="1"
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              onBlur={handleInputBlur}
              onKeyDown={handleKeyDown}
              className="w-16 px-2 py-1 text-right border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="ml-1 text-gray-600 dark:text-gray-300">%</span>
          </div>
        ) : (
          <button
            onClick={() => onEditStart(metric.id)}
            className="px-3 py-1 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-full text-sm font-medium transition-colors"
          >
            {weight}%
          </button>
        )}
      </td>
      <td className="px-4 py-3 w-48">
        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-500 rounded-full transition-all duration-300"
            style={{ width: `${weight}%` }}
          />
        </div>
      </td>
    </tr>
  );
}

/**
 * Tenant row showing custom weight status
 */
function TenantRow({ tenant }) {
  return (
    <tr className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800">
      <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">{tenant.tenantName}</td>
      <td className="px-4 py-3">
        {tenant.hasCustomWeights ? (
          <Badge variant="blue">Custom Weights</Badge>
        ) : (
          <Badge variant="gray">Using Defaults</Badge>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
        {tenant.updatedAt
          ? new Date(tenant.updatedAt).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : '—'}
      </td>
    </tr>
  );
}

/**
 * Admin Pricing Rules page
 * Allows super-admins to set industry default weights and view tenant customizations
 */
export function AdminPricingRules() {
  const [activeTab, setActiveTab] = useState('defaults');
  const [metrics, setMetrics] = useState([]);
  const [weights, setWeights] = useState({});
  const [originalWeights, setOriginalWeights] = useState({});
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [editingMetricId, setEditingMetricId] = useState(null);

  const hasChanges = JSON.stringify(weights) !== JSON.stringify(originalWeights);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [industryData, tenantsData] = await Promise.all([
        settingsApi.getIndustryDefaults('commercial_construction'),
        settingsApi.getTenantsWithCustomWeights(),
      ]);

      setMetrics(industryData.availableMetrics);
      setWeights(industryData.metricWeights);
      setOriginalWeights(industryData.metricWeights);
      setTenants(tenantsData.tenants);
    } catch (err) {
      setError('Failed to load pricing rules. Please try again.');
      console.error('Error loading admin pricing rules:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleWeightChange = useCallback((metricId, newValue) => {
    setWeights(prev => normalizeWeights(prev, metricId, newValue));
    setSuccess(false);
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await settingsApi.updateIndustryDefaults('commercial_construction', weights);
      setOriginalWeights(weights);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Failed to save changes. Please try again.');
      console.error('Error saving industry defaults:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setWeights(originalWeights);
    setSuccess(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const weightTotal = Object.values(weights).reduce((sum, w) => sum + w, 0);
  const tenantsWithCustom = tenants.filter(t => t.hasCustomWeights).length;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Admin: Pricing Rules</h1>
        <p className="text-gray-600 dark:text-gray-300 mt-1">
          Configure industry default weights and view tenant customizations.
        </p>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 flex items-center">
          <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          {error}
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300 flex items-center">
          <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          Default weights saved successfully!
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200 dark:border-gray-800">
        <nav className="flex gap-8">
          <button
            onClick={() => setActiveTab('defaults')}
            className={`py-3 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'defaults'
                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-100'
            }`}
          >
            Industry Defaults
          </button>
          <button
            onClick={() => setActiveTab('tenants')}
            className={`py-3 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'tenants'
                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-100'
            }`}
          >
            Tenant Customizations
            {tenantsWithCustom > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs rounded-full">
                {tenantsWithCustom}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'defaults' && (
        <>
          {/* Info Card */}
          <Card className="mb-6 bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800">
            <CardContent className="py-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div>
                  <h4 className="text-sm font-medium text-yellow-900 dark:text-yellow-200">Admin Settings</h4>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                    These are the default weights applied to new tenants. Existing tenants who haven't customized their weights will also use these defaults.
                    Changes here affect all tenants using default weights.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Weights Table */}
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Commercial Construction - Default Weights</CardTitle>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Click percentages to edit. Total must equal 100%.
                  </p>
                </div>
                <div className={`text-sm font-medium ${Math.abs(weightTotal - 100) < 0.1 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  Total: {weightTotal.toFixed(1)}%
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Metric
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-32">
                      Weight
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-48">
                      Distribution
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.map((metric) => (
                    <MetricRow
                      key={metric.id}
                      metric={metric}
                      weight={weights[metric.id] || 0}
                      onWeightChange={handleWeightChange}
                      isEditing={editingMetricId === metric.id}
                      onEditStart={setEditingMetricId}
                      onEditEnd={() => setEditingMetricId(null)}
                    />
                  ))}
                </tbody>
              </table>
            </CardContent>
            <CardFooter>
              <div className="flex items-center justify-between w-full">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {hasChanges && 'You have unsaved changes'}
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    onClick={handleDiscard}
                    disabled={!hasChanges || saving}
                  >
                    Discard Changes
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={!hasChanges || saving}
                    loading={saving}
                  >
                    Save Defaults
                  </Button>
                </div>
              </div>
            </CardFooter>
          </Card>
        </>
      )}

      {activeTab === 'tenants' && (
        <Card>
          <CardHeader>
            <CardTitle>Tenant Weight Customizations</CardTitle>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {tenantsWithCustom} of {tenants.length} tenants have customized their weights.
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Tenant
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Last Modified
                  </th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((tenant) => (
                  <TenantRow key={tenant.tenantId} tenant={tenant} />
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default AdminPricingRules;
